import React, {Suspense, useEffect, lazy, useState, useCallback} from 'react';
import {useTranslation} from 'react-i18next';

import {
	// Button,
	CloseIcon,
	Dialog,
	Divider,
	IconButton,
	Layout,
	Text,
	WarningFilledIcon
} from '@phonectas/unifon-ui-kit';
import {StyledSimCardGrid} from '../../styled';
import UserInfoList from './UserInfoList';
import SimCardPlaceholderIcon from 'common/components/icons/SimCardPlaceholderIcon';
import ReportErrorBtn from 'common/components/ReportErrorBtn';
import ReportErrorContent from './ReportErrorContent';
import useThreeSixtyNewStore from 'common/stores/threeSixtyStore';
import {shallow} from 'zustand/shallow';
import {CustomerUser} from 'common/types/store/threeSixtyTypes';
import LinearLoader from 'common/components/customLoader/components/LinearLoader';
// import ReleaseSimContent from './ReleaseSimContent';

// Lazy load SimCardContainer
const SimCardContainer = lazy(() => import('common/components/SimCardContainer'));

interface UserDialogContentProps {
	onClose: () => void;
	data: CustomerUser;
}

const UserDialogContent: React.FC<UserDialogContentProps> = ({onClose, data}) => {
	const [t] = useTranslation();
	const {
		getUserParentId,
		reportIncident,
		getAccountFeed,
	} = useThreeSixtyNewStore((state) => ({
		getUserParentId: state.getUserParentId,
		reportIncident: state.reportIncident,
		getAccountFeed: state.getAccountFeed,
	}), shallow);
	const [reportDialog, setReportDialog] = useState(false);
	// const [releaseSimDialog, setReleaseSimDialog] = useState(false);
	const parentData = useThreeSixtyNewStore((state) => state.accountTables?.parentId);
	const isWarning = typeof data?.note === 'string' && data.note.trim() !== '';
	const defaultForm = React.useMemo(() => ({
		category: '',
		country: '',
		description: '',
		user_id: '',
	}), []);
	// const handleCloseReleaseSimDialog = useCallback(() => {
	// 	setReleaseSimDialog(false);
	// }, []);

	const [form, setForm] = useState(defaultForm);
	const handleFormChange = useCallback((name: string, value: string) => {
		setForm((prevForm) => ({
			...prevForm,
			[name]: value,
		}));
	}, []);
	const handleShowReportDialog = useCallback(() => {
		setReportDialog(true);
	}, []);

	const handleCloseReportDialog = useCallback(() => {
		setReportDialog(false);
	}, []);

	const handleSendReportError = useCallback(() => {
		if (!form || !form.category || !form.country || !form.description || !form.user_id) {
			return;
		}
		reportIncident({
			data: form,
			onSuccess: () => {
				setReportDialog(false);
				setForm(defaultForm);
				setTimeout(() => {
					getAccountFeed({id: data.account_id});
				}, 1000);
			}
		});
	}, [form, reportIncident, defaultForm, data?.account_id, getAccountFeed]);

	const validateForm = useCallback(() => {
		const {category, country, description} = form;
		return !!category && !!country && !!description;
	}, [form]);

	useEffect(() => {
		setForm((prevForm) => ({
			...prevForm,
			user_id: data?.id,
		}));
	}, [data?.id]);

	useEffect(() => {
		if (data?.cdr_children_count > 0) {
			getUserParentId({
				accountId: data.account_id,
				parent: data.cdr_subscription_id,
				includeChild: true,
				limit: 20,
			});
		}
	}, [data?.cdr_subscription_id, data?.account_id, data?.cdr_children_count, getUserParentId]);

	return (
		<>
			<Layout nowrap alignItems="start" fullWidth justifyContent="space-between">
				<Layout nowrap fullWidth alignItems="center" justifyContent="space-between">
					<Layout direction="column" fullWidth nowrap gap="none">
						<Layout alignItems="center" gap="sm" fullWidth>
							<Text variant="h1">
								{data?.name}
							</Text>
							{
								isWarning && (
									<WarningFilledIcon color="error" />
								)
							}
						</Layout>
					</Layout>
					{/* <Button
						variant="caption"
						onClick={() => setReleaseSimDialog(true)}
					>
						{t('Release SIM')}
					</Button> */}
					<ReportErrorBtn onClick={handleShowReportDialog}/>
					<IconButton onClick={onClose}><CloseIcon /></IconButton>
				</Layout>
			</Layout>
			<Divider />
			<UserInfoList data={data} />
			<Divider style={{marginBottom: '10px'}} />
			{
				data?.cdr_children_count > 0 ? (
					<Layout fullWidth direction="column">
						<Text variant="h4">
							{t('Additional SIMs')}: {data?.cdr_children_count}
						</Text>
						<Suspense
							fallback={<LinearLoader isLoading={true} />}
						>
							<StyledSimCardGrid style={{overflow: 'hidden'}}>
								{
									parentData?.customer_users?.map((item, index) => (
										<SimCardContainer key={index} data={item} />
									))
								}
							</StyledSimCardGrid>
						</Suspense>
					</Layout>
				) : (
					<Layout fullWidth justifyContent="center" direction="column">
						<Layout>
							<Text variant="h5">
								{t('Additional SIMs')}
							</Text>
						</Layout>
						<Layout fullWidth justifyContent="center" alignItems="center">
							<Layout fullWidth justifyContent="center" alignItems="center">
								<SimCardPlaceholderIcon />
							</Layout>
							<Layout fullWidth justifyContent="center" alignItems="center">
								<Text variant="body1">
									{t('No additional SIM cards')}
								</Text>
							</Layout>
						</Layout>
					</Layout>
				)
			}
			<Dialog
				show={reportDialog}
				id="report-dialog"
				onOk={handleSendReportError}
				onClose={handleCloseReportDialog}
				closeButtonLabel={t('Close')}
				title={t('Report Incident')}
				disableOk={!validateForm()}
				style={{width: '540px'}}
			>
				<ReportErrorContent
					formData={form}
					onChange={handleFormChange}
					data={data}
				/>
			</Dialog>
			{/* <Dialog
				show={releaseSimDialog}
				id="release-sim-dialog"
				style={{padding: '12px'}}
				onClose={handleCloseReleaseSimDialog}
			>
				<ReleaseSimContent
					onClose={handleCloseReleaseSimDialog}
					data={data}
					extraSimCards={parentData?.customer_users}
				/>
			</Dialog> */}
		</>
	);
}

export default React.memo(UserDialogContent);
